import React, { useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Phone, ShieldCheck, Loader2, MapPin } from "lucide-react";

/** Recherche + choix d'un numéro Twilio. `endpoint` = base API (chef: /chef/talk, admin: /admin/talk) ; `onPick(number)` déclenche l'achat. */
export function NumberSearch({ endpoint, countries = {}, onPick, picking, pricing }) {
  const [country, setCountry] = useState("FR");
  const [kind, setKind] = useState("Local");
  const [contains, setContains] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${endpoint}/numbers/search`, { params: { country, contains, kind } });
      setResults(data.numbers);
      if (!data.numbers.length) toast.info("Aucun numéro disponible avec ces critères");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setResults([]); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="talk-number-search">
      <div className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-11 px-3 text-sm border border-[#DEE2E6] bg-white" data-testid="talk-search-country">{Object.entries(countries).map(([k, l]) => <option key={k} value={k}>{l} ({k})</option>)}</select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-11 px-3 text-sm border border-[#DEE2E6] bg-white" data-testid="talk-search-kind"><option value="Local">Fixe / géographique</option><option value="Mobile">Mobile</option><option value="TollFree">Numéro vert</option></select>
        <Input value={contains} onChange={(e) => setContains(e.target.value)} placeholder="Chiffres souhaités (ex. 01 ou 0975)" className="h-11" data-testid="talk-search-contains" />
        <Button onClick={search} disabled={loading} className="h-11 bg-[#0A0A0A] text-white" data-testid="talk-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}<span className="ml-2">Rechercher</span></Button>
      </div>
      {pricing && <div className="text-xs text-[#495057]">Abonnement du numéro : <b>{pricing.number_monthly} €/mois</b> · appels entrants {pricing.inbound_per_min} €/min · sortants {pricing.outbound_per_min} €/min (facturés avec votre abonnement PAG).</div>}
      {results && results.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="talk-search-results">
          {results.map((n) => (
            <div key={n.phone_number} className="border border-[#DEE2E6] bg-white p-3 flex items-center gap-3" data-testid={`talk-number-${n.phone_number}`}>
              <Phone className="h-4 w-4 text-[#002FA7]" />
              <div className="flex-1 min-w-0"><div className="font-mono font-bold">{n.display}</div><div className="text-[11px] text-[#868E96] flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{[n.locality, n.region].filter(Boolean).join(", ") || n.kind}{n.address_required ? " · justificatif requis" : ""}</div></div>
              <Button size="sm" onClick={() => onPick(n.phone_number)} disabled={!!picking} className="h-9 bg-[#059669] hover:bg-[#047857] text-white" data-testid={`talk-pick-${n.phone_number}`}>{picking === n.phone_number ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Choisir"}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Carte du numéro actif de l'entreprise. */
export function NumberCard({ number, opening }) {
  return (
    <div className="bg-white border-2 border-[#0A0A0A] p-5 flex flex-wrap items-center gap-4" data-testid="talk-number-card">
      <span className="h-12 w-12 flex items-center justify-center bg-[#002FA7] text-white"><Phone className="h-6 w-6" /></span>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#002FA7] font-bold">Votre numéro professionnel</div>
        <div className="font-display font-black text-2xl lg:text-3xl tracking-tight" data-testid="talk-number-display">{number.display}</div>
        <div className="text-xs text-[#495057] mt-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-[#059669]" />Actif depuis le {new Date(number.created_at).toLocaleDateString("fr-FR")} · {number.country || "—"}</div>
      </div>
      {opening && <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 ${opening.open ? "bg-[#059669] text-white" : "bg-[#E60000] text-white"}`} data-testid="talk-opening-badge">{opening.open ? "Standard ouvert" : "Standard fermé"}</span>}
    </div>
  );
}
